/**
 * Entidade Plan: um documento = plano gratuito OU plano pago (premium)
 * com variantes mensal / semestral / anual (preço + Stripe).
 *
 * Subscription referencia `plan_id` em `users/{uid}/subscriptions/current`.
 */

import { z } from 'zod';

export const planKindSchema = z.enum(['free', 'paid']);
export type PlanKind = z.infer<typeof planKindSchema>;

export const planBillingPeriodSchema = z.enum(['monthly', 'semiannual', 'annual']);
export type PlanBillingPeriod = z.infer<typeof planBillingPeriodSchema>;

export const PLAN_BILLING_PERIODS: readonly PlanBillingPeriod[] = [
  'monthly',
  'semiannual',
  'annual',
];

export const PLAN_KIND_LABELS: Record<PlanKind, string> = {
  free: 'Gratuito',
  paid: 'Pago (Premium)',
};

export const PLAN_BILLING_PERIOD_LABELS: Record<PlanBillingPeriod, string> = {
  monthly: 'Mensal',
  semiannual: 'Semestral',
  annual: 'Anual',
};

export const PLAN_BILLING_PERIOD_MONTHS: Record<PlanBillingPeriod, number> = {
  monthly: 1,
  semiannual: 6,
  annual: 12,
};

const stripePriceIdSchema = z
  .string()
  .regex(/^price_[a-zA-Z0-9]+$/, 'ID inválido: use price_… do Dashboard Stripe')
  .nullable()
  .optional();

export const planStripeSchema = z.object({
  stripe_price_id: stripePriceIdSchema,
  stripe_price_id_test: stripePriceIdSchema,
  stripe_price_lookup_key: z.string().min(1).nullable().optional(),
  stripe_price_lookup_key_test: z.string().min(1).nullable().optional(),
});

export type PlanStripe = z.infer<typeof planStripeSchema>;

export const planBillingVariantSchema = z.object({
  price_brl_cents: z.number().int().min(0),
  stripe: planStripeSchema,
});

export type PlanBillingVariant = z.infer<typeof planBillingVariantSchema>;

export type PlanBillingVariants = Partial<Record<PlanBillingPeriod, PlanBillingVariant>>;

/** Limites do plano gratuito (apenas `kind: free`). */
export const planFreeLimitsSchema = z.object({
  max_services: z.number().int().min(0),
});

export type PlanFreeLimits = z.infer<typeof planFreeLimitsSchema>;

export const planSchema = z.object({
  id: z.string().uuid(),
  kind: planKindSchema,
  title: z.string().min(1),
  sort_order: z.number().int().min(0),
  is_active: z.boolean(),
  billing_variants: z.record(planBillingPeriodSchema, planBillingVariantSchema).optional(),
  free_limits: planFreeLimitsSchema.optional(),
  created_at: z.string(),
  updated_at: z.string(),
});

export type Plan = z.infer<typeof planSchema>;

export class PlanRef {
  static readonly collection = 'plans';

  static docPath(id: string): string {
    return `${this.collection}/${id}`;
  }
}

const EMPTY_STRIPE: PlanStripe = {
  stripe_price_id: null,
  stripe_price_id_test: null,
  stripe_price_lookup_key: null,
  stripe_price_lookup_key_test: null,
};

function normalizeStripe(raw: unknown): PlanStripe {
  const o =
    raw && typeof raw === 'object' && !Array.isArray(raw) ?
      (raw as Record<string, unknown>)
    : {};

  const pickId = (key: string): string | null | undefined => {
    const v = o[key];
    if (v === null) return null;
    if (typeof v !== 'string') return undefined;
    const t = v.trim();
    if (t.length === 0) return undefined;
    return /^price_[a-zA-Z0-9]+$/.test(t) ? t : undefined;
  };

  const pickKey = (key: string): string | null | undefined => {
    const v = o[key];
    if (v === null) return null;
    if (typeof v !== 'string') return undefined;
    const t = v.trim();
    return t.length > 0 ? t : undefined;
  };

  return {
    stripe_price_id: pickId('stripe_price_id') ?? null,
    stripe_price_id_test: pickId('stripe_price_id_test') ?? null,
    stripe_price_lookup_key: pickKey('stripe_price_lookup_key') ?? null,
    stripe_price_lookup_key_test: pickKey('stripe_price_lookup_key_test') ?? null,
  };
}

function normalizeVariant(raw: unknown): PlanBillingVariant | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const priceRaw = o.price_brl_cents;
  const price_brl_cents =
    typeof priceRaw === 'number' && Number.isInteger(priceRaw) && priceRaw >= 0 ? priceRaw : 0;
  return {
    price_brl_cents,
    stripe: normalizeStripe(o.stripe ?? o),
  };
}

/**
 * Normaliza documento Firestore antes do parse Zod.
 */
export function normalizePlanFields(
  id: string,
  raw: Record<string, unknown>
): Record<string, unknown> {
  const kindParsed = planKindSchema.safeParse(raw.kind);
  let kind: PlanKind = kindParsed.success ? kindParsed.data : 'paid';

  if (!kindParsed.success && raw.internal_free === true) {
    kind = 'free';
  }

  const titleRaw = raw.title ?? raw.name;
  const title = typeof titleRaw === 'string' && titleRaw.trim().length > 0 ? titleRaw.trim() : 'Plano';

  const sortRaw = raw.sort_order;
  const sort_order =
    typeof sortRaw === 'number' && Number.isInteger(sortRaw) && sortRaw >= 0 ? sortRaw : 0;

  const is_active = raw.is_active === false ? false : true;

  let billing_variants: PlanBillingVariants | undefined;
  const variantsRaw = raw.billing_variants;
  if (variantsRaw && typeof variantsRaw === 'object' && !Array.isArray(variantsRaw)) {
    billing_variants = {};
    for (const period of PLAN_BILLING_PERIODS) {
      const v = normalizeVariant((variantsRaw as Record<string, unknown>)[period]);
      if (v) billing_variants[period] = v;
    }
    if (Object.keys(billing_variants).length === 0) {
      billing_variants = undefined;
    }
  } else if (kind === 'paid' && raw.billing_period && raw.price_brl_cents != null) {
    const periodParsed = planBillingPeriodSchema.safeParse(raw.billing_period);
    if (periodParsed.success) {
      billing_variants = {
        [periodParsed.data]: {
          price_brl_cents:
            typeof raw.price_brl_cents === 'number' ? raw.price_brl_cents : 0,
          stripe: normalizeStripe(raw),
        },
      };
    }
  }

  let free_limits: PlanFreeLimits | undefined;
  const freeLimitsRaw = raw.free_limits;
  if (freeLimitsRaw && typeof freeLimitsRaw === 'object' && !Array.isArray(freeLimitsRaw)) {
    const maxRaw = (freeLimitsRaw as Record<string, unknown>).max_services;
    if (typeof maxRaw === 'number' && Number.isInteger(maxRaw) && maxRaw >= 0) {
      free_limits = { max_services: maxRaw };
    }
  }

  const created_at = typeof raw.created_at === 'string' ? raw.created_at : new Date().toISOString();
  const updated_at = typeof raw.updated_at === 'string' ? raw.updated_at : created_at;

  return {
    id,
    kind,
    title,
    sort_order,
    is_active,
    ...(kind === 'paid' && billing_variants ? { billing_variants } : {}),
    ...(kind === 'free' && free_limits ? { free_limits } : {}),
    created_at,
    updated_at,
  };
}

export function emptyPaidBillingVariants(): Record<PlanBillingPeriod, PlanBillingVariant> {
  return {
    monthly: { price_brl_cents: 0, stripe: { ...EMPTY_STRIPE } },
    semiannual: { price_brl_cents: 0, stripe: { ...EMPTY_STRIPE } },
    annual: { price_brl_cents: 0, stripe: { ...EMPTY_STRIPE } },
  };
}

export const STRIPE_PRICE_ID_PATTERN = /^price_[a-zA-Z0-9]+$/;

export function isFreePlan(plan: Plan): boolean {
  return plan.kind === 'free';
}

export function formatPlanPriceBrlFromCents(cents: number): string {
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function parseBrlMoneyStringToCents(input: string): number | null {
  const t = input.trim();
  if (t === '') return 0;
  const only = t.replace(/\s/g, '');
  const lastComma = only.lastIndexOf(',');
  const lastDot = only.lastIndexOf('.');
  let normalized: string;
  if (lastComma > lastDot) {
    normalized = only.replace(/\./g, '').replace(',', '.');
  } else {
    normalized = only.replace(/,/g, '');
  }
  const n = Number.parseFloat(normalized);
  if (Number.isNaN(n) || n < 0) return null;
  return Math.round(n * 100);
}

export function planPriceCentsToFormString(cents: number): string {
  return (cents / 100).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
